import {
  AspectRatio,
  BackgroundImage,
  Box,
  Grid,
  Group,
  Image,
  Text,
} from '@mantine/core';
import { AnimatePresence, motion } from 'framer-motion';
import React, { FC } from 'react';
import styled from 'styled-components';

import { PortalConfigType } from '@portals/api/partners';
import { PoweredByXyteIcon, SignIn } from '@portals/framework';
import { FADE_IN_OUT } from '@portals/ui';

interface SignInBrowserMockProps {
  portalConfig: Partial<PortalConfigType>;
}

const SignInBrowserMock: FC<SignInBrowserMockProps> = ({ portalConfig }) => (
  <Grid
    sx={(theme) => ({
      height: '100%',
      backgroundColor: theme.white,
      position: 'relative',
      pointerEvents: 'none',
    })}
    m={0}
  >
    <Grid.Col
      lg={3}
      md={3}
      p={0}
      sx={(theme) => ({
        backgroundColor:
          portalConfig.design?.sign_in_page?.background_color ||
          theme.colors.blue_accent[4],
      })}
    >
      <BackgroundImage
        src={
          portalConfig?.design?.sign_in_page?.background_mode === 'image' &&
          portalConfig?.design?.sign_in_page?.background_image
        }
        sx={{
          height: '100%',
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {portalConfig?.design?.sign_in_page?.logo ? (
          <Logo
            logo={portalConfig?.design?.sign_in_page.logo}
            className="auth-logo"
          />
        ) : null}

        <Group
          style={{ width: 200, transform: 'translateY(-50px)' }}
          position="center"
        >
          <AspectRatio
            ratio={1}
            sx={{
              maxWidth: 200,
              width: '100%',
            }}
            mb="md"
          >
            <Image
              src={
                portalConfig?.design?.sign_in_page?.login_image ||
                'logo_full.svg'
              }
              mb="lg"
            />
          </AspectRatio>

          {portalConfig?.design?.sign_in_page?.tagline ? (
            <Text
              align="center"
              sx={(theme) => ({
                color:
                  portalConfig?.design?.sign_in_page?.tagline_color ||
                  theme.white,
                fontWeight: 600,
                fontSize: theme.fontSizes.md,
                lineHeight: '30px',
                letterSpacing: '0.01em',
                padding: '0 10px',
                wordBreak: 'break-word',
              })}
            >
              {portalConfig?.design?.sign_in_page?.tagline ||
                'Supercharging your HaaS business'}
            </Text>
          ) : null}
        </Group>

        <AnimatePresence>
          <Box
            sx={{
              height: 18,
              position: 'absolute',
              bottom: 20,
            }}
          >
            {portalConfig.design?.sign_in_page?.powered_by_xyte_label !==
            false ? (
              <motion.div {...FADE_IN_OUT}>
                <PoweredByXyteIcon
                  style={{
                    mixBlendMode: portalConfig?.design?.sign_in_page
                      ?.background_color
                      ? 'difference'
                      : 'unset',
                  }}
                />
              </motion.div>
            ) : null}
          </Box>
        </AnimatePresence>
      </BackgroundImage>
    </Grid.Col>

    <Grid.Col lg={8} md={9}>
      <Group sx={{ height: '100%', width: '100%' }} grow position="center">
        <ContentWrapper>
          <SignIn isPreview config={portalConfig} />
        </ContentWrapper>
      </Group>
    </Grid.Col>
  </Grid>
);

const ContentWrapper = styled.div`
  max-width: 410px !important;
  width: 410px;
  padding: 0 35px;

  .tos-container {
    font-size: 12px;
  }
`;

const Logo = styled.div<{ logo: string }>`
  position: absolute;
  top: 15px;
  left: 15px;
  width: 30px;
  height: 30px;
  background-image: url(${({ logo }) => logo});
  background-position: left;
  background-size: contain;
  background-repeat: no-repeat;
`;

export default SignInBrowserMock;
